// Shared regime sojourn bar — used by BOTH the Scanner and the Screener.
// Answers: "will I likely still be in this regime at expiry, or will it shift?"
// by comparing days-elapsed-in-regime vs the historical median sojourn, with a
// DTE tick mark. One implementation, two consumers.

const REGIME_COLORS = {
  "Quiet Range": "#64748b",
  "Bullish Trend": "#22c55e",
  "Bearish Trend": "#f43f5e",
  Volatile: "#f59e0b",
};

export function RegimeSojournBar({ regime, elapsedDays, medianSojournDays, dte, transitionProbs }) {
  // No data → render nothing (never crash, never a placeholder).
  if (!medianSojournDays || elapsedDays === null || elapsedDays === undefined) return null;

  const median = Math.max(1, Math.round(medianSojournDays));
  const elapsed = Math.max(0, Math.round(elapsedDays));
  const dteN = Math.max(0, Math.round(dte || 0));
  const color = REGIME_COLORS[regime] || "#64748b";

  const overextended = elapsed >= median;
  const remaining = median - elapsed;
  const fillPct = Math.min(100, (elapsed / median) * 100);
  const dtePct = Math.min(100, (dteN / median) * 100);

  // Line-3 signal (always visible — never hidden behind hover).
  let signal, signalColor;
  if (overextended) {
    signal = "Regime overextended — transition risk elevated";
    signalColor = "#f59e0b";
  } else if (dteN > remaining) {
    signal = "Regime may shift before expiry";
    signalColor = "#f59e0b";
  } else {
    signal = `Regime likely holds ~${remaining}d more`;
    signalColor = "#22c55e";
  }

  const tp = transitionProbs || {};
  const fmt = (v) => (v === null || v === undefined ? "–" : v);
  const fits = dteN <= remaining && !overextended;
  const tooltip =
    `Currently ${elapsed}d into ${regime}. Median duration: ~${median}d. ` +
    `Your DTE of ${dteN}d ${fits ? "fits within" : "exceeds"} expected regime duration.\n\n` +
    `Transition probabilities from ${regime}:\n` +
    `→ Quiet ${fmt(tp.quiet)}% / → Bullish ${fmt(tp.bullish)}%\n` +
    `→ Bearish ${fmt(tp.bearish)}% / → Volatile ${fmt(tp.volatile)}%`;

  return (
    <div className="mt-1 w-full max-w-[200px]" data-testid="regime-sojourn-bar">
      <div className="flex items-center gap-1.5" title={tooltip}>
        <div className="relative flex-1 rounded-full" style={{ height: 6, background: `${color}33`, minWidth: 70 }}>
          <div className="absolute left-0 top-0 h-full rounded-full" style={{ width: `${fillPct}%`, background: color }} />
          {/* DTE tick — 2px white line, extends 3px above/below the bar */}
          <div
            className="absolute"
            data-testid="sojourn-dte-tick"
            style={{ left: `calc(${dtePct}% - 1px)`, top: -3, height: 12, width: 2, background: "#ffffff", borderRadius: 1, boxShadow: "0 0 2px rgba(0,0,0,0.6)" }}
          />
        </div>
        <span className="text-[11px] tabular-nums whitespace-nowrap" style={{ color: "var(--hoip-text-tertiary)" }}>
          {elapsed}d / ~{median}d{overextended ? "+" : ""}
        </span>
      </div>
      <div className="text-[11px] mt-0.5 leading-tight" style={{ color: signalColor }} data-testid="sojourn-signal">
        {signal}
      </div>
    </div>
  );
}
